/*
 * This file is part of glowingblue/orell-bookshelf.
 *
 * Copyright (c) 2022 Glowing Blue AG.
 * Authors: Rafael Horvat, Priska.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import Component, { type ComponentAttrs } from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Placeholder from 'flarum/common/components/Placeholder';
import type Mithril from 'mithril';
import type { ApiResponseSingle } from 'flarum/common/Store';
import isOwnBookshelf from '../helpers/isOwnBookshelf';
import BookListState from '../states/BookListState';
import BookListItem from './BookListItem';
import BookshelfSort from './BookshelfSort';
import type Bookshelf from '../../common/models/Bookshelf';

export interface BookListAttrs extends ComponentAttrs {
	bookshelf: ApiResponseSingle<Bookshelf>;
	parentLoading?: boolean;
}

export default class BookList<CustomAttrs extends BookListAttrs = BookListAttrs> extends Component<CustomAttrs, BookListState | undefined> {
	oninit(vnode: Mithril.Vnode<CustomAttrs, this>) {
		super.oninit(vnode);

		const { parentLoading } = this.attrs;
		if (!parentLoading) {
			this.initState();
		}
	}

	onupdate(vnode: Mithril.VnodeDOM<CustomAttrs, this>) {
		super.onupdate(vnode);

		const { parentLoading } = this.attrs;
		if (!this.state && !parentLoading) {
			this.initState();
		}
	}

	initState() {
		const { bookshelf } = this.attrs;

		this.state = new BookListState({
			filter: { bookshelf: bookshelf.id() },
			sort: m.route.param('sort'),
		});

		app.bookListState = this.state;

		this.state.initFirstPage(bookshelf);
	}

	view(): Mithril.Children {
		const state = this.state;
		const { bookshelf, parentLoading } = this.attrs;

		let loading;

		if (!state || state.isInitialLoading() || state.isLoadingNext() || parentLoading) {
			loading = <LoadingIndicator />;
		} else if (state.hasNext()) {
			loading = (
				<Button className="Button" onclick={state.loadNext.bind(state)}>
					{app.translator.trans('core.forum.discussion_list.load_more_button')}
				</Button>
			);
		}

		if (!state) {
			return loading;
		}

		return (
			<div className="BookList">
				<BookshelfSort state={state} prfx="glowingblue-orell-bookshelf.forum.bookshelf.sort" />
				<div className="BookList--items-container">
					{!parentLoading &&
						state.getPages().map((pg) => {
							return pg.items.map((article) => <BookListItem article={article} />);
						})}
					{state && state.isEmpty() && !parentLoading && isOwnBookshelf(bookshelf) && (
						<Placeholder text={app.translator.trans('glowingblue-orell-bookshelf.forum.bookshelf.empty_instructions')} />
					)}
				</div>

				<div className="BookList-loadMore">{loading}</div>
			</div>
		);
	}
}
